// FundInvestmentDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { 
    Building2, 
    DollarSign,
    Target,
    Calendar,
    Users
} from 'lucide-react';
import { useParams } from "react-router";
import './FundInvestmentDetailPage.css';
import { formatCurrency, formatDateIntl, formatInterest } from "../../util/currency_lookup";
import { Loader } from '../../components/loader';

const FundUserDrillDownPage = () => {
  const [fund, setFund] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState({
    fund: true,
    investors: true
  });
  const [error, setError] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    fetchFund();
    fetchInvestors();
  }, [id]);

  const fetchFund = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/funds/by-id/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch fund');
      }

      const data = await response.json();
      setFund(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, fund: false }));
    }
  };

  const fetchInvestors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/funds/investment/${id}/user-metrics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch investors');
      }

      const data = await response.json();
      setInvestors(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, investors: false }));
    }
  };

  const isLoading = Object.values(loading).some(status => status);

  if (isLoading) {
    return <Loader loading={true} />;
  }

  if (error) return <div className="error">{error}</div>;
  if (!fund) return <div className="error">No fund found</div>;

  return (
    <div className="fund-investment-detail">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <h1>Fund Investment Details</h1>
          <div className="fund-name">{fund.name} ({fund.symbol})</div>
        </div>
      </div>

      {/* Basic Fund Information */}
      <div className="overview">
        <div className="overview-card">
          <div className="overview-title">Fund Information</div>
          <div className="details-grid">
            <div className="detail-card">
              <div className="detail-card-content">
                <Building2 className="detail-icon" />
                <div>
                  <div className="detail-label">Fund Name</div>
                  <div className="detail-value">{fund.name}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <DollarSign className="detail-icon" />
                <div>
                  <div className="detail-label">Currency</div>
                  <div className="detail-value">{fund.currency_code}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <Target className="detail-icon" />
                <div>
                  <div className="detail-label">Minimum Investment</div>
                  <div className="detail-value">{formatCurrency(fund.min_value, fund.currency_code)}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <Calendar className="detail-icon" />
                <div>
                  <div className="detail-label">Created At</div>
                  <div className="detail-value">{formatDateIntl(fund.created_at)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Investors Table */}
      <div className="investors-section">
        <div className="section-header">
          <h2>
            <Users className="section-icon" />
            Fund Investors
          </h2>
          <div className="total-investors">
            Total Investors: {investors.length}
          </div>
        </div>

        <div className="table-container">
          <table className="investors-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Net Investment</th>
                <th>Total Investment</th>
                <th>Total Redemption</th>
                <th>Transactions</th>
                <th>Largest Investment</th>
                <th>% of Fund</th>
              </tr>
            </thead>
            <tbody>
              {investors.map((investor) => (
                <tr key={investor.user_id}>
                  <td>
                    {investor.first_name} {investor.last_name}
                  </td>
                  <td>{investor.email}</td>
                  <td className={investor.net_investment >= 0 ? 'positive' : 'negative'}>
                    {formatCurrency(investor.net_investment, fund.currency_code)}
                  </td>
                  <td>
                    {formatCurrency(investor.total_investments, fund.currency_code)}
                  </td>
                  <td>
                    {formatCurrency(investor.total_redemptions, fund.currency_code)}
                  </td>
                  <td>{investor.total_transactions}</td>
                  <td>
                    {formatCurrency(investor.largest_investment, fund.currency_code)}
                  </td>
                  <td>
                    {investor.percentage_of_fund+"%"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FundUserDrillDownPage;
